import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { type StatusBarItem, useWorkspacePage } from "@/components/workspace";
import { PartyService } from "@/gen/omnios/v1/party_connect.js";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useServerGrid } from "@/lib/use-server-grid";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useGridSelection } from "@/hooks/use-grid-selection";
import { createClient } from "@connectrpc/connect";
import { useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { Building2, Plus, Search, User } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/parties/")({
	component: PartiesIndexPage,
});

const client = createClient(PartyService, transport);

interface PartyRow {
	id: string;
	kind: string;
	name: string;
	displayName: string;
	isInternal: boolean;
	roles: string[];
}

function useKindLabel() {
	const { t } = useTranslation("parties");
	return useCallback(
		(kind: string): string => {
			switch (kind) {
				case "PERSON":
					return t("kind_person");
				case "ORGANIZATION":
					return t("kind_organization");
				case "TEAM":
					return t("kind_team");
				case "AGENT":
					return t("kind_agent");
				default:
					return kind;
			}
		},
		[t],
	);
}

function kindBadgeColor(kind: string): string {
	switch (kind) {
		case "PERSON":
			return "hsl(var(--chart-1))";
		case "ORGANIZATION":
			return "#7c3aed";
		case "TEAM":
			return "#0891b2";
		case "AGENT":
			return "#d97706";
		default:
			return "hsl(var(--muted-foreground))";
	}
}

// Map proto enum int values to string names
function kindIntToStr(v: number): string {
	const map: Record<number, string> = {
		0: "UNSPECIFIED",
		1: "PERSON",
		2: "ORGANIZATION",
		3: "TEAM",
		4: "AGENT",
	};
	return map[v] ?? "UNSPECIFIED";
}

function PartiesIndexPage() {
	const { t } = useTranslation("parties");
	const kindLabel = useKindLabel();
	useBreadcrumbs([{ label: t("breadcrumb") }]);
	const navigate = useNavigate();
	const qc = useQueryClient();
	const { onGridReady, onStateChanged } = useGridLayout("parties");

	const [createOpen, setCreateOpen] = useState(false);
	const [searchText, setSearchText] = useState("");

	const routeSearch = useSearch({ from: "/parties" });
	const roleFilter = (routeSearch as Record<string, unknown>).role as
		| string
		| undefined;

	const [deleting, setDeleting] = useState(false);

	const { gridRef, gridProps, totalRows, refreshGrid } =
		useServerGrid<PartyRow>({
			fetchRows: async ({ pageSize, pageToken, sort, search }) => {
				const resp = await client.listParties({
					page: { pageSize, pageToken },
					sort: sort
						? {
								field: sort.field === "isInternal" ? "is_internal" : sort.field,
								descending: sort.descending,
							}
						: undefined,
					search: search ?? "",
					role: roleFilter || undefined,
				});
				return {
					rows: resp.parties.map((p) => ({
						id: p.id,
						kind: kindIntToStr(p.kind),
						name: p.name,
						displayName: p.displayName,
						isInternal: p.isInternal,
						roles: p.roles ?? [],
					})),
					nextPageToken: resp.page?.nextPageToken,
					totalCount: resp.page?.totalCount ?? 0,
				};
			},
			pageSize: 100,
			search: searchText,
			colToField: {
				isInternal: "is_internal",
			},
		});

	const { selectedRows, hasSelection, onSelectionChanged, ActionBar } =
		useGridSelection<PartyRow>({
			entityLabel: t("total_label", { defaultValue: "контрагентов" }),
		});

	const invalidatePartySummaries = useCallback(() => {
		qc.invalidateQueries({ queryKey: ["parties", "overview"] });
		qc.invalidateQueries({ queryKey: ["stats", "parties"] });
	}, [qc]);

	const handleDelete = useCallback(async () => {
		setDeleting(true);
		try {
			await client.bulkDeleteParties({
				ids: [...selectedRows.map((r) => r.id)],
				all: false,
			});
			gridRef.current?.api?.deselectAll();
			invalidatePartySummaries();
			refreshGrid();
			toast.success(t("bulk_delete_success"));
		} catch {
			toast.error(t("bulk_delete_error"));
		} finally {
			setDeleting(false);
		}
	}, [refreshGrid, gridRef, invalidatePartySummaries, selectedRows, t]);

	// ── AG Grid column definitions ──
	const columnDefs = useMemo<ColDef<PartyRow>[]>(
		() => [
			{
				colId: "checkbox",
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "name",
				headerName: t("col_name"),
				flex: 2,
				minWidth: 200,
				pinned: "left",
				cellRenderer: (p: { data: PartyRow | undefined }) => {
					if (!p.data) return "";
					const display = p.data.displayName || p.data.name;
					return (
						<div style={{ display: "flex", alignItems: "center" }}>
							{p.data.kind === "ORGANIZATION" ? (
								<svg
									width="14"
									height="14"
									viewBox="0 0 24 24"
									fill="none"
									stroke="hsl(var(--muted-foreground))"
									strokeWidth="2"
									style={{ marginRight: 6, flexShrink: 0 }}
								>
									<rect x="3" y="3" width="18" height="18" rx="2" />
									<path d="M9 3v18" />
									<path d="M3 9h18" />
								</svg>
							) : (
								<svg
									width="14"
									height="14"
									viewBox="0 0 24 24"
									fill="none"
									stroke="hsl(var(--muted-foreground))"
									strokeWidth="2"
									style={{ marginRight: 6, flexShrink: 0 }}
								>
									<circle cx="12" cy="8" r="5" />
									<path d="M20 21a8 8 0 0 0-16 0" />
								</svg>
							)}
							<span style={{ fontWeight: 500 }}>{display}</span>
							{p.data.displayName && p.data.displayName !== p.data.name && (
								<span
									style={{
										color: "hsl(var(--muted-foreground))",
										fontSize: 11,
										marginLeft: 4,
									}}
								>
									({p.data.name})
								</span>
							)}
						</div>
					);
				},
			},
			{
				field: "kind",
				headerName: t("col_kind"),
				width: 130,
				cellRenderer: (p: { data: PartyRow | undefined }) => {
					if (!p.data) return "";
					const color = kindBadgeColor(p.data.kind);
					return (
						<span
							style={{
								display: "inline-block",
								padding: "1px 6px",
								borderRadius: 4,
								fontSize: 11,
								lineHeight: "18px",
								background: `color-mix(in srgb, ${color} 10%, transparent)`,
								color,
							}}
						>
							{kindLabel(p.data.kind)}
						</span>
					);
				},
			},
			{
				field: "isInternal",
				headerName: t("col_internal", { defaultValue: "Внутренний" }),
				width: 110,
				cellRenderer: (p: { data: PartyRow | undefined; value: boolean }) => {
					if (!p.data || !p.value) return "";
					return (
						<span
							style={{
								display: "inline-block",
								padding: "1px 6px",
								border: "1px solid hsl(var(--border))",
								borderRadius: 4,
								fontSize: 11,
								lineHeight: "18px",
							}}
						>
							{t("is_internal_yes", { defaultValue: "Да" })}
						</span>
					);
				},
			},
			{
				colId: "roles",
				headerName: t("col_roles"),
				width: 180,
				valueGetter: (p) => (p.data?.roles ?? []).join(", "),
				cellRenderer: (p: { data: PartyRow | undefined }) => {
					const roles = p.data?.roles ?? [];
					if (!roles.length) return "";
					return (
						<span>
							{roles.map((r) => (
								<span
									key={r}
									style={{
										display: "inline-block",
										padding: "1px 6px",
										border: "1px solid hsl(var(--border))",
										borderRadius: 4,
										fontSize: 10,
										lineHeight: "16px",
										marginRight: 4,
									}}
								>
									{t(`role_${r}`, r)}
								</span>
							))}
						</span>
					);
				},
			},
		],
		[t, kindLabel],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const onRowClicked = useCallback(
		(e: { data: PartyRow | undefined }) => {
			if (e.data) {
				navigate({ to: "/parties/$id", params: { id: e.data.id } });
			}
		},
		[navigate],
	);

	// ── Status bar ──
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: hasSelection
					? `${selectedRows.length} ${t("selected_label", { defaultValue: "выбрано" })}`
					: `${totalRows} ${t("total_label", { defaultValue: "контрагентов" })}`,
				align: "left" as const,
			},
		],
		[hasSelection, selectedRows.length, totalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", {
							defaultValue: "Поиск контактов...",
						})}
						className="pl-9 h-9"
						onChange={(e) => setSearchText(e.target.value)}
					/>
				</div>
				{hasSelection && (
					<Button
						variant="outline"
						size="sm"
						className="h-9"
						disabled={deleting}
						onClick={handleDelete}
					>
						{t("bulk_delete_action", { defaultValue: "Удалить" })}
					</Button>
				)}
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({ fileName: "parties.csv" })
					}
				>
					{t("export", { defaultValue: "Экспорт" })}
				</Button>
				<Button size="sm" className="h-9" onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("new_party")}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				<AgGridReact<PartyRow>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onRowClicked={onRowClicked}
					onGridReady={onGridReady}
					onSortChanged={() => {
						onStateChanged();
						refreshGrid();
					}}
					onFilterChanged={() => {
						onStateChanged();
						refreshGrid();
					}}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					rowClass="cursor-pointer"
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data.id}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_no_parties_title")}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_no_parties_description")}</p></div>`}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading")}...</span>`}
				/>
			</div>

			<ActionBar />
			<CreatePartyDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				onCreated={async (partyId) => {
					invalidatePartySummaries();
					refreshGrid();
					navigate({ to: "/parties/$id", params: { id: partyId } });
				}}
				defaultRole={roleFilter}
			/>
		</div>
	);
}

function KindCard({
	selected,
	onClick,
	icon,
	label,
}: {
	selected: boolean;
	onClick: () => void;
	icon: React.ReactNode;
	label: string;
}) {
	return (
		<Button
			type="button"
			variant="outline"
			className={`flex flex-col items-center gap-2 p-4 h-auto border-2 ${
				selected
					? "border-primary bg-primary/5"
					: "border-muted hover:border-muted-foreground/30"
			}`}
			onClick={onClick}
		>
			<div className={selected ? "text-primary" : "text-muted-foreground"}>
				{icon}
			</div>
			<p className="text-sm font-medium">{label}</p>
		</Button>
	);
}

function CreatePartyDialog({
	open,
	onOpenChange,
	onCreated,
	defaultRole,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onCreated: (partyId: string) => void | Promise<void>;
	defaultRole?: string;
}) {
	const { t } = useTranslation("parties");
	const [name, setName] = useState("");
	const [displayName, setDisplayName] = useState("");
	const [kind, setKind] = useState("1");
	const [selectedRoles, setSelectedRoles] = useState<Set<string>>(
		() => new Set(defaultRole ? [defaultRole] : []),
	);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Sync default role when dialog opens in a new context
	useEffect(() => {
		if (open) setSelectedRoles(new Set(defaultRole ? [defaultRole] : []));
	}, [open, defaultRole]);

	const toggleRole = (role: string) => {
		setSelectedRoles((prev) => {
			const next = new Set(prev);
			if (next.has(role)) next.delete(role);
			else next.add(role);
			return next;
		});
	};

	const handleSubmit = async () => {
		if (!name.trim()) return;
		setSaving(true);
		setError(null);
		try {
			const party = await client.createParty({
				name: name.trim(),
				displayName: displayName.trim(),
				kind: Number.parseInt(kind) as any,
				isInternal: false,
			});
			for (const role of selectedRoles) {
				await client.addPartyRole({ partyId: party.id, role });
			}
			setName("");
			setDisplayName("");
			setKind("1");
			setSelectedRoles(new Set());
			toast.success(t("create_success"));
			onOpenChange(false);
			await onCreated(party.id);
		} catch (e) {
			setError(e instanceof Error ? e.message : t("create_failed"));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{defaultRole
							? t(`add_${defaultRole}`, t("create_dialog_title"))
							: t("create_dialog_title")}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="space-y-1.5">
						<Label>{t("label_type")}</Label>
						<div className="grid grid-cols-2 gap-2">
							<KindCard
								selected={kind === "1"}
								onClick={() => setKind("1")}
								icon={<User className="h-5 w-5" />}
								label={t("kind_person")}
							/>
							<KindCard
								selected={kind === "2"}
								onClick={() => setKind("2")}
								icon={<Building2 className="h-5 w-5" />}
								label={t("kind_organization")}
							/>
						</div>
					</div>
					<div className="space-y-1.5">
						<Label>{t("label_name")}</Label>
						<Input
							placeholder={t("placeholder_name")}
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
					</div>
					<div className="space-y-1.5">
						<Label>{t("label_display_name")}</Label>
						<Input
							placeholder={t("placeholder_optional")}
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
						/>
					</div>
					<div className="space-y-1.5">
						<Label>{t("label_roles")}</Label>
						<div className="flex flex-wrap gap-3">
							{(["customer", "supplier", "competitor"] as const).map((r) => (
								<Label
									key={r}
									className="flex items-center gap-1.5 cursor-pointer"
								>
									<Checkbox
										checked={selectedRoles.has(r)}
										onCheckedChange={() => toggleRole(r)}
									/>
									<span className="text-sm">{t(`role_${r}`)}</span>
								</Label>
							))}
						</div>
					</div>
				</div>
				{error && <p className="text-sm text-destructive">{error}</p>}
				<DialogFooter>
					<Button onClick={handleSubmit} disabled={!name.trim() || saving}>
						{saving ? t("creating") : t("common:create")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
